import { useEffect, useRef, useState, useCallback } from "react";

import axios from "axios";
import { io, Socket } from "socket.io-client";

import { orgSessionManager } from "@domains/auth/services/orgSessionManager";
import { ApplicationEndpoints } from "@generated/application";
import { apiBaseUrls } from "@platform/constants/shared-constants";

import { useStackRealtime } from "./useStackRealtime";

// ── Types (mirror backend k8s-event.types.ts) ─────────────────────────────────

export interface K8sEventSnapshot {
  name: string;
  namespace: string;
  involvedObjectName: string;
  involvedObjectKind: string;
  reason: string;
  message: string;
  type: "Normal" | "Warning";
  count: number;
  firstTimestamp: string | null;
  lastTimestamp: string | null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

/** Polling interval (ms) when WebSocket is disconnected */
const POLL_INTERVAL_MS = 10_000;

export interface UseK8sEventsResult {
  events: K8sEventSnapshot[];
  loading: boolean;
  error: string | null;
  connected: boolean;
  refresh: () => void;
}

/**
 * Real-time K8s events hook.
 *
 * 1. On mount: fetches initial events via `GET /api/v1/applications/:alias/events`
 * 2. Opens a Socket.io connection to `/pods` namespace
 * 3. Subscribes via `subscribeEvents` — backend streams K8sEventSnapshot[] on each watch event
 * 4. Falls back to REST polling every 10s when WebSocket is disconnected
 * 5. On unmount: disconnects the socket
 */
export function useK8sEvents(alias: string | undefined): UseK8sEventsResult {
  // ── Context consumption ──────────────────────────────────────────────────
  const ctx = useStackRealtime();

  // ── Fallback: always declare hooks to satisfy rules-of-hooks ──────────
  const [events, setEvents] = useState<K8sEventSnapshot[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const socketRef = useRef<Socket | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const token = () => orgSessionManager.getToken() ?? "";

  const fetchEvents = useCallback(async () => {
    if (!alias) return;
    setLoading(true);
    setError(null);
    try {
      const url = `${apiBaseUrls.BACKEND}${ApplicationEndpoints.GET_APPLICATION_EVENTS.replace(":id", alias)}`;
      const res = await axios.get<K8sEventSnapshot[]>(url, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setEvents(res.data);
    } catch {
      setError("Failed to load events.");
    } finally {
      setLoading(false);
    }
  }, [alias]);

  useEffect(() => {
    if (!alias) return;

    void fetchEvents();

    // Open WebSocket — connect to the /pods namespace
    const socket = io(`${apiBaseUrls.BACKEND}/pods`, {
      auth: { token: token() },
      transports: ["polling", "websocket"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 20,
    });
    socketRef.current = socket;

    socket.on("connect", () => {
      setConnected(true);
      socket.emit("subscribeEvents", { alias });
      // Stop polling while connected
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    });

    socket.on("events", (data: K8sEventSnapshot[]) => {
      setEvents(data);
      setError(null);
    });

    socket.on("error", (data: { message: string }) => {
      setError(data.message);
    });

    socket.on("disconnect", () => {
      setConnected(false);
      // Start polling fallback when disconnected
      if (!pollRef.current) {
        void fetchEvents();
        pollRef.current = setInterval(
          () => void fetchEvents(),
          POLL_INTERVAL_MS,
        );
      }
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [alias, fetchEvents]);

  // ── Context short-circuit: return shared data if available ──────────────
  if (ctx) {
    return {
      events: ctx.k8sEvents,
      loading: ctx.k8sEventsLoading,
      error: ctx.k8sEventsError,
      connected: ctx.podsConnected,
      refresh: ctx.refetchK8sEvents,
    };
  }

  return { events, loading, error, connected, refresh: fetchEvents };
}
